(() => {
  /** Jenny's story switches between the founder profile and her product vision. */
  const initJenny = (root, signal) => {
    const section = root.querySelector('[data-jenny-carousel]');
    const nextButton = section?.querySelector('[data-jenny-next]');
    const previousButton = section?.querySelector('[data-jenny-previous]');
    const profile = section?.querySelector('.about-jenny__row');
    const insights = section?.querySelector('.about-jenny__insights');
    if (!section || !nextButton || !previousButton) return;

    const showInsights = (visible, moveFocus = true) => {
      section.classList.toggle('is-insights', visible);
      nextButton.setAttribute('aria-pressed', String(visible));
      profile?.setAttribute('aria-hidden', String(visible));
      insights?.setAttribute('aria-hidden', String(!visible));
      if (moveFocus) (visible ? previousButton : nextButton).focus();
    };

    nextButton.addEventListener('click', () => showInsights(true), { signal });
    previousButton.addEventListener('click', () => showInsights(false), { signal });

    // Keep the arrow controls, while also supporting the native left/right
    // gesture expected on touch devices.
    let touchStartX = null;
    let touchStartY = null;
    const slides = section.querySelector('[data-jenny-slides]');
    slides?.addEventListener('touchstart', (event) => {
      const touch = event.changedTouches[0];
      touchStartX = touch?.clientX ?? null;
      touchStartY = touch?.clientY ?? null;
    }, { passive: true, signal });
    slides?.addEventListener('touchend', (event) => {
      if (touchStartX === null || touchStartY === null) return;
      const touch = event.changedTouches[0];
      const deltaX = (touch?.clientX ?? touchStartX) - touchStartX;
      const deltaY = (touch?.clientY ?? touchStartY) - touchStartY;
      touchStartX = null;
      touchStartY = null;
      if (Math.abs(deltaX) < 40 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
      if (deltaX < 0) showInsights(true, false);
      else showInsights(false, false);
    }, { passive: true, signal });
    slides?.addEventListener('touchcancel', () => {
      touchStartX = null;
      touchStartY = null;
    }, { passive: true, signal });
    showInsights(false, false);
  };

  /** What We Build is a three-card carousel with a centered active card. */
  const initBuildCarousel = (root, signal) => {
    const carousel = root.querySelector('[data-build-carousel]');
    if (!carousel) return;

    const cards = [...carousel.querySelectorAll('[data-build-card]')]
      .sort((first, second) => Number(first.dataset.buildOrder) - Number(second.dataset.buildOrder));
    const previousButton = carousel.querySelector('[data-build-previous]');
    const nextButton = carousel.querySelector('[data-build-next]');
    const viewport = carousel.querySelector('.about-build__viewport');
    if (cards.length < 2 || !previousButton || !nextButton) return;

    let activeIndex = Math.min(1, cards.length - 1);
    const update = () => {
      cards.forEach((card, index) => {
        const offset = index - activeIndex;
        card.style.left = `${50 + offset * 50.4226042}%`;
        card.classList.toggle('is-active', offset === 0);
        card.classList.toggle('is-previous', offset === -1);
        card.classList.toggle('is-next', offset === 1);
        card.classList.toggle('is-hidden', Math.abs(offset) > 1);
        card.setAttribute('aria-hidden', String(index !== activeIndex));
      });
      previousButton.disabled = activeIndex === 0;
      nextButton.disabled = activeIndex === cards.length - 1;
    };

    previousButton.addEventListener('click', (event) => {
      event.currentTarget.blur();
      if (activeIndex === 0) return;
      activeIndex -= 1;
      update();
      requestAnimationFrame(() => previousButton.blur());
    }, { signal });
    nextButton.addEventListener('click', (event) => {
      event.currentTarget.blur();
      if (activeIndex === cards.length - 1) return;
      activeIndex += 1;
      update();
      requestAnimationFrame(() => nextButton.blur());
    }, { signal });

    const clearButtonFocus = (event) => {
      // Touch UAs can keep the pressed/focus state after the pointer is up.
      if (event.pointerType === 'touch' || event.type === 'touchend') {
        event.currentTarget.blur();
      }
    };
    [previousButton, nextButton].forEach((button) => {
      button.addEventListener('pointerup', clearButtonFocus, { signal });
      button.addEventListener('touchend', clearButtonFocus, { passive: true, signal });
    });

    // Keep the arrow controls, while allowing a native horizontal swipe on
    // the mobile viewport.
    let touchStartX = null;
    let touchStartY = null;
    viewport?.addEventListener('touchstart', (event) => {
      const touch = event.changedTouches[0];
      touchStartX = touch?.clientX ?? null;
      touchStartY = touch?.clientY ?? null;
    }, { passive: true, signal });
    viewport?.addEventListener('touchend', (event) => {
      if (touchStartX === null || touchStartY === null) return;
      const touch = event.changedTouches[0];
      const deltaX = (touch?.clientX ?? touchStartX) - touchStartX;
      const deltaY = (touch?.clientY ?? touchStartY) - touchStartY;
      touchStartX = null;
      touchStartY = null;
      if (Math.abs(deltaX) < 40 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
      if (deltaX < 0 && activeIndex < cards.length - 1) activeIndex += 1;
      if (deltaX > 0 && activeIndex > 0) activeIndex -= 1;
      update();
    }, { passive: true, signal });
    viewport?.addEventListener('touchcancel', () => {
      touchStartX = null;
      touchStartY = null;
    }, { passive: true, signal });
    update();
  };

  /** Lifecycle-safe component for Shopify's live theme editor. */
  class BeamboxAbout extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;
      this.abortController = new AbortController();
      initJenny(this, this.abortController.signal);
      initBuildCarousel(this, this.abortController.signal);
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.abortController = null;
    }
  }

  if (!customElements.get('beambox-about')) {
    customElements.define('beambox-about', BeamboxAbout);
  }
})();
